import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Hojita ligera auto-height para el menú móvil de FormSelect.
 *
 * A diferencia del BottomSheet compartido (skeleton fill-and-scroll con
 * contenedor height:100dvh + flex-items con overflow que NO encogen en iOS
 * Safari), esta hoja es una caja `height:auto` con `max-height` y un único
 * `overflow-y-auto` — el mismo patrón del popover desktop, que SÍ hace hug en
 * iOS. Encoge al contenido cuando es corto; scrollea solo si excede 70vh.
 * Header sticky para que quede fijo al hacer scroll en listas largas.
 */
export default function MenuSheet({ isOpen, onClose, title, children }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    // Scroll-lock robusto para iOS (mismo patrón que MemberKeviaSheet):
    // overflow:hidden en body NO basta en iOS Safari — se fija el body con
    // top:-scrollY y se restaura el scroll al cerrar.
    const y = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top      = `-${y}px`;
    document.body.style.width    = "100%";

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    dialogRef.current?.focus();

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top      = "";
      document.body.style.width    = "";
      window.scrollTo(0, y);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px] animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* SHEET — anclada abajo, height:auto + max-h, único overflow-y-auto (hugea en iOS) */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="absolute left-0 right-0 bottom-0 max-h-[70vh] overflow-y-auto overscroll-contain bg-page-bg rounded-t-[28px] shadow-2xl outline-none animate-in slide-in-from-bottom-full duration-300 ease-out"
        style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
      >
        {/* HEADER STICKY (gripper + título + X) */}
        <div className="sticky top-0 z-10 bg-page-bg">
          <div className="w-full flex justify-center pt-3 pb-1">
            <div className="h-1.5 w-12 bg-slate-300 dark:bg-slate-600 rounded-full" />
          </div>
          <div className="px-5 pb-3 flex items-center gap-3">
            <h3 className="flex-1 min-w-0 text-[18px] font-bold text-slate-900 dark:text-slate-100 truncate">
              {title}
            </h3>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="h-11 w-11 rounded-full bg-slate-200/50 dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 transition-colors active:bg-slate-200 dark:active:bg-slate-700 shrink-0"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-3">{children}</div>
      </div>
    </div>,
    document.body
  );
}
